'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';
import ButtonV2 from 'components/shared/ButtonV2';
import CustomLink from 'components/shared/CustomLink';

import { scienceLandingAsset } from './asset';
import { useReveal } from './useReveal';
import styles from './AccuracyStandard.module.css';

/* §6 — "How accurate is your ring?" (Figma 894:9470). A left-aligned header
   (eyebrow · two-line heading, the second line greyed · "View all studies"),
   then a row of portrait photo cards that starts at the content edge and bleeds
   off the right. Each card: title + reference line at the top over a shade, a
   glass pill with the result in the middle, "Learn more ↗" at the bottom; the
   whole card links to the studies page. Embla drives the row (drag/swipe), and
   the dots under it double as a 4 s autoplay timer on desktop (paused off-screen,
   on hover and while dragging). Phones keep the same layout with narrower cards. */

const MD_MIN_PX = 992;
const DESKTOP_MQ = `(min-width: ${MD_MIN_PX}px)`;
const MOTION_OK_MQ = '(prefers-reduced-motion: no-preference)';
const AUTOPLAY_S = 4;
const canAutoplay = () =>
  window.matchMedia(DESKTOP_MQ).matches && window.matchMedia(MOTION_OK_MQ).matches;
const img = (name: string) => scienceLandingAsset(`accuracy/acc7-${name}.jpg`);

export default function AccuracyStandard() {
  const t = useTranslations('science');
  const outerRef = useRef<HTMLElement | null>(null);
  const progressRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const progressTween = useRef<gsap.core.Tween | null>(null);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoplayPausedRef = useRef(false);

  /* keys spelled out so check-i18n-keys / the payload manifest can see them */
  const cards = useMemo(
    () => [
      {
        id: 'sleep',
        slug: 'sleep-duration',
        image: 'sleep',
        title: t('science.landing.accuracy.cards.sleep.title'),
        reference: t('science.landing.accuracy.cards.sleep.reference'),
        result: t('science.landing.accuracy.cards.sleep.result'),
        resultLabel: t('science.landing.accuracy.cards.sleep.resultLabel'),
        imageAlt: t('science.landing.accuracy.cards.sleep.imageAlt'),
      },
      {
        id: 'temperature',
        slug: 'temperature-sensor',
        image: 'temperature',
        title: t('science.landing.accuracy.cards.temperature.title'),
        reference: t('science.landing.accuracy.cards.temperature.reference'),
        result: t('science.landing.accuracy.cards.temperature.result'),
        resultLabel: t('science.landing.accuracy.cards.temperature.resultLabel'),
        imageAlt: t('science.landing.accuracy.cards.temperature.imageAlt'),
      },
      {
        id: 'heart',
        slug: 'longevity-afib',
        image: 'heart-rhythm',
        title: t('science.landing.accuracy.cards.heart.title'),
        reference: t('science.landing.accuracy.cards.heart.reference'),
        result: t('science.landing.accuracy.cards.heart.result'),
        resultLabel: t('science.landing.accuracy.cards.heart.resultLabel'),
        imageAlt: t('science.landing.accuracy.cards.heart.imageAlt'),
      },
      {
        id: 'womensHealth',
        slug: 'cycle-ovulation',
        image: 'ovulation',
        title: t('science.landing.accuracy.cards.womensHealth.title'),
        reference: t('science.landing.accuracy.cards.womensHealth.reference'),
        result: t('science.landing.accuracy.cards.womensHealth.result'),
        resultLabel: t('science.landing.accuracy.cards.womensHealth.resultLabel'),
        imageAlt: t('science.landing.accuracy.cards.womensHealth.imageAlt'),
      },
      {
        id: 'fitness',
        slug: 'longevity-vo2max',
        image: 'vo2max',
        title: t('science.landing.accuracy.cards.fitness.title'),
        reference: t('science.landing.accuracy.cards.fitness.reference'),
        result: t('science.landing.accuracy.cards.fitness.result'),
        resultLabel: t('science.landing.accuracy.cards.fitness.resultLabel'),
        imageAlt: t('science.landing.accuracy.cards.fitness.imageAlt'),
      },
      {
        id: 'bloodOxygen',
        slug: 'spo2-sp-hypoxia',
        image: 'blood-oxygen',
        title: t('science.landing.accuracy.cards.bloodOxygen.title'),
        reference: t('science.landing.accuracy.cards.bloodOxygen.reference'),
        result: t('science.landing.accuracy.cards.bloodOxygen.result'),
        resultLabel: t('science.landing.accuracy.cards.bloodOxygen.resultLabel'),
        imageAlt: t('science.landing.accuracy.cards.bloodOxygen.imageAlt'),
      },
    ],
    [t],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', containScroll: 'trimSnaps', dragFree: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(cards.length);
  const selectedIndexRef = useRef(0);
  selectedIndexRef.current = selectedIndex;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setSnapCount(emblaApi.scrollSnapList().length);
  }, [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // ── autoplay with the active dot filling as the timer (wraps to the start) ──
  const killProgressTween = useCallback(() => {
    progressTween.current?.kill();
    progressTween.current = null;
  }, []);
  const clearAutoplayTimer = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);
  const resetBars = useCallback((fillActive: boolean, active: number) => {
    progressRefs.current.forEach((el, i) => {
      if (el) gsap.set(el, { width: fillActive && i === active ? '100%' : '0%' });
    });
  }, []);
  const stopAutoplay = useCallback(() => {
    clearAutoplayTimer();
    killProgressTween();
  }, [clearAutoplayTimer, killProgressTween]);
  const startAutoplay = useCallback(
    (active: number) => {
      if (!emblaApi || isAutoplayPausedRef.current || !canAutoplay()) {
        resetBars(true, active);
        return;
      }
      clearAutoplayTimer();
      killProgressTween();
      resetBars(false, active);
      const el = progressRefs.current[active];
      if (el) {
        gsap.set(el, { width: '0%' });
        progressTween.current = gsap.to(el, { width: '100%', duration: AUTOPLAY_S, ease: 'none' });
      }
      autoplayTimerRef.current = setTimeout(() => {
        if (emblaApi.canScrollNext()) emblaApi.scrollNext();
        else emblaApi.scrollTo(0);
      }, AUTOPLAY_S * 1000);
    },
    [emblaApi, clearAutoplayTimer, killProgressTween, resetBars],
  );
  useEffect(() => {
    if (!emblaApi) return undefined;
    startAutoplay(selectedIndex);
    return () => stopAutoplay();
  }, [emblaApi, selectedIndex, startAutoplay, stopAutoplay]);

  // pause off-screen
  useEffect(() => {
    const node = outerRef.current;
    if (!emblaApi || !node) return undefined;
    let tid: ReturnType<typeof setTimeout>;
    const io = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(tid);
        tid = setTimeout(() => {
          isAutoplayPausedRef.current = !entry?.isIntersecting;
          if (entry?.isIntersecting) startAutoplay(selectedIndexRef.current);
          else stopAutoplay();
        }, 150);
      },
      { threshold: 0.3, rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(node);
    return () => {
      clearTimeout(tid);
      io.disconnect();
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  // pause while hovered (desktop) and while dragging
  useEffect(() => {
    const node = outerRef.current;
    if (!emblaApi || !node) return undefined;
    const onEnter = () => {
      if (!canAutoplay()) return;
      isAutoplayPausedRef.current = true;
      stopAutoplay();
    };
    const onLeave = () => {
      isAutoplayPausedRef.current = false;
      startAutoplay(selectedIndexRef.current);
    };
    const onDown = () => stopAutoplay();
    const onUp = () => startAutoplay(selectedIndexRef.current);
    node.addEventListener('mouseenter', onEnter);
    node.addEventListener('mouseleave', onLeave);
    emblaApi.on('pointerDown', onDown).on('pointerUp', onUp);
    return () => {
      node.removeEventListener('mouseenter', onEnter);
      node.removeEventListener('mouseleave', onLeave);
      emblaApi.off('pointerDown', onDown).off('pointerUp', onUp);
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const desktop = window.matchMedia(DESKTOP_MQ);
    const motion = window.matchMedia(MOTION_OK_MQ);
    const onChange = () => startAutoplay(selectedIndexRef.current);
    desktop.addEventListener('change', onChange);
    motion.addEventListener('change', onChange);
    return () => {
      desktop.removeEventListener('change', onChange);
      motion.removeEventListener('change', onChange);
    };
  }, [emblaApi, startAutoplay]);

  // header lines, then the cards in view, then the dots, fade up as they enter
  useReveal(outerRef);

  const carouselLabel = t('science.landing.accuracy.aria.carousel');

  return (
    <section ref={outerRef} className={styles.outer} aria-labelledby="science-accuracy-title">
      <div className={styles.head}>
        <p className={styles.eyebrow} data-reveal>
          {t('science.landing.accuracy.eyebrow')}
        </p>
        <h2 id="science-accuracy-title" className={styles.title} data-reveal>
          {t('science.landing.accuracy.titleLine1')}
          <br />
          <span className={styles.titleMuted}>{t('science.landing.accuracy.titleLine2')}</span>
        </h2>
        <div data-reveal>
          <ButtonV2 href={{ pathname: "/science/studies/", hash: "accuracy-studies" }} variant="black" label={t('science.landing.accuracy.viewAllStudies')} />
        </div>
      </div>

      <div ref={emblaRef} className={styles.viewport} role="region" aria-roledescription="carousel" aria-label={carouselLabel}>
        <ul className={styles.track}>
          {cards.map((c, i) => (
            <li key={c.id} className={styles.slide} aria-roledescription="slide" data-reveal>
              <CustomLink
                href={`/science/accuracy/${c.slug}/`}
                className={styles.card}
                aria-label={t('science.landing.accuracy.aria.openStudy', { name: c.title })}
              >
                <Image
                  className={styles.cardImg}
                  src={img(c.image)}
                  alt={c.imageAlt}
                  fill
                  sizes="(min-width: 992px) 385px, 300px"
                  priority={i === 0}
                />
                <span className={styles.scrim} aria-hidden="true" />
                <span className={styles.top}>
                  <span className={styles.cardTitle}>{c.title}</span>
                  <span className={styles.reference}>{c.reference}</span>
                </span>
                <span className={styles.pill}>
                  <span className={styles.result}>{c.result}</span>
                  <span className={styles.resultLabel}>{c.resultLabel}</span>
                </span>
                <span className={styles.learn} aria-hidden="true">
                  {t('science.landing.accuracy.learnMore')}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M4.666 11.333 11.333 4.667" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.666 4.667h6.667v6.666" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </CustomLink>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.dots} role="group" aria-label={carouselLabel} data-reveal>
        {Array.from({ length: snapCount }).map((_, i) => {
          const selected = i === selectedIndex;
          const name = cards[i]?.title ?? String(i + 1);
          return (
            <button
              key={i}
              type="button"
              className={`${styles.dot} ${selected ? styles.dotActive : ''}`}
              aria-label={t('science.landing.accuracy.aria.goToSlide', { name })}
              aria-current={selected ? 'true' : undefined}
              onClick={() => scrollTo(i)}
            >
              <span
                className={styles.dotFill}
                ref={(n) => {
                  progressRefs.current[i] = n;
                }}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}
